"use client";

import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";

import { usePathname } from "next/navigation";

import {
  elementLeavesPage,
  hrefLeavesPage,
  subscribeNavigationStarted,
} from "@/utils/buttonNavigation";

const MAX_PENDING_MS = 15000;

function isModifiedClick(event: MouseEvent<HTMLElement>) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export function useClickedButtonPending() {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (pathnameRef.current === pathname) return;
    pathnameRef.current = pathname;
    setPending(false);
  }, [pathname]);

  useEffect(() => {
    if (!pending) return;
    const id = window.setTimeout(() => setPending(false), MAX_PENDING_MS);
    return () => window.clearTimeout(id);
  }, [pending]);

  /*
   * Cache Components hides the previous route instead of unmounting it. Cleanup still runs, so
   * this clears leftover pending state that would otherwise come back disabled on back/forward.
   */
  useLayoutEffect(() => {
    return () => setPending(false);
  }, []);

  const holdForDestination = (href: string) => {
    if (typeof window !== "undefined" && hrefLeavesPage(href, window.location.href)) {
      setPending(true);
    }
  };

  const runClick = (event: MouseEvent<HTMLElement>, run: () => void) => {
    let started = false;
    const unsubscribe = subscribeNavigationStarted(() => {
      started = true;
    });

    try {
      run();
    } finally {
      unsubscribe();
    }

    const followsLink =
      !isModifiedClick(event) && !event.defaultPrevented && elementLeavesPage(event.currentTarget);

    if (started || followsLink) {
      setPending(true);
    }
  };

  return { pending, runClick, holdForDestination };
}
